import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { seoReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/seoExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/seoExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/seoExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;
    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function SeoService() {
  const safeReviews = Array.isArray(seoReviews) ? seoReviews : [];
  const title = "SEO — On-Page SEO, Structure, Titles, Content Clarity";
  const description =
    "Improve search visibility with SEO: better page titles, headings, structure, content clarity, and foundational on-page optimization.";
  const canonical = "https://brainwavesolutions.net/services/seo";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "SEO", url: canonical },
    ]),
    buildServiceSchema({
      name: "SEO",
      description,
      canonical,
      offerLabel: "$50+",
      serviceType: "On-page SEO and content structure improvements",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "SEO page audit example showing improved page titles, headings, and on-page structure.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Search engine optimization example showing improved content hierarchy and internal linking structure.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "SEO improvement example showing page structure, metadata updates, and clearer keyword targeting.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                SEO that help your site become easier to find — and easier to understand
              </Typography>

              <p className="bws-gsl-lead">
                A lot of websites struggle with SEO before they ever get into advanced tactics. Page titles are weak,
                headings are vague, content is not structured clearly, and the site does not make it easy for search
                engines — or people — to understand what each page is about.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help strengthen the fundamentals: page titles, headings, on-page structure, clarity, internal linking,
                and content organization. The goal is not gimmicks — it is building a cleaner foundation that supports
                better visibility and a better user experience.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">On-page SEO</span>
                <span className="bws-gsl-badge">Titles &amp; headings</span>
                <span className="bws-gsl-badge">Content structure</span>
                <span className="bws-gsl-badge">Better visibility</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with SEO
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common SEO problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Page titles and headings that do not clearly target the topic</li>
                  <li>Content that is hard for both users and search engines to understand</li>
                  <li>Weak page structure and poor internal linking</li>
                  <li>Service pages that do not match search intent well</li>
                  <li>Sites that need a stronger foundation before advanced SEO makes sense</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with your website URL, the type of traffic you want, and which pages matter most.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Strengthen the foundation</Typography>
              <Typography className="bws-gsl-stripText">
                Better titles, headings, structure, and page clarity create a stronger SEO base.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Match search intent better</Typography>
              <Typography className="bws-gsl-stripText">
                Help pages line up more clearly with what people are actually searching for.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Improve usability too</Typography>
              <Typography className="bws-gsl-stripText">
                Good SEO usually makes the page easier for real visitors to read and trust as well.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of page structure, content, and on-page SEO improvements.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Page titles, meta descriptions, and heading structure</li>
                    <li>Service page and landing page SEO</li>
                    <li>Content organization and on-page clarity</li>
                    <li>Internal linking and structure improvements</li>
                    <li>Making pages easier to understand for both users and search engines</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses that want stronger visibility fundamentals</li>
                    <li>Service pages that are not ranking or converting well</li>
                    <li>Sites with unclear page structure or weak targeting</li>
                    <li>Anyone wanting practical SEO improvements without unnecessary complexity</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Do you do full SEO campaigns?</Typography>
                <Typography className="bws-gsl-faqA">
                  My focus here is on practical SEO and on-page improvements — the foundation that often needs to be fixed first.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you improve existing pages instead of building new ones?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. Many SEO improvements come from strengthening pages that already exist rather than starting over.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the site, the pages that matter most, and what kind of traffic or visibility you want to improve.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help improving your SEO foundation?</Typography>
              <p className="bws-muted">
                Send a quick description of your site and which pages you want to improve.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
